import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { formatCustomerUsers } from "../../../../lib/customer/users";
import { formatCustomerPhone } from "../../../../lib/customer/phone";
import type { Customer } from "../../../../services/customerService";

type MetaItemProps = {
  label: string;
  value?: string | null;
};

const MetaItem = ({ label, value }: MetaItemProps) => {
  if (!value || !String(value).trim()) {
    return null;
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary" noWrap title={String(value)}>
        {value}
      </Typography>
    </Box>
  );
};

type CustomerDetailsReadOnlyProps = {
  customer: Customer;
};

const CustomerDetailsReadOnly = ({ customer }: CustomerDetailsReadOnlyProps) => {
  const { t } = useTranslation();
  const logoSrc = customer.logo
    ? customer.logo.startsWith("data:")
      ? customer.logo
      : `data:image/png;base64,${customer.logo}`
    : null;

  const statusColor =
    customer.status === "ACTIVE"
      ? "success"
      : customer.status === "INACTIVE"
        ? "error"
        : customer.status === "SUSPENDED"
          ? "warning"
          : "default";

  const workTime =
    customer.startWorkTime && customer.endWorkTime
      ? `${customer.startWorkTime} - ${customer.endWorkTime}`
      : undefined;
  const usersLabel = formatCustomerUsers(customer.users ?? customer.username);

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 2,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
      }}
    >
      {logoSrc && (
        <Box
          component="img"
          src={logoSrc}
          alt={customer.name || "Customer logo"}
          sx={{ height: 44, width: 44, objectFit: "contain", borderRadius: 1, flexShrink: 0 }}
        />
      )}

      <Box sx={{ minWidth: 160, flex: "1 1 180px" }}>
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {customer.name || "Unnamed customer"}
          </Typography>
          {customer.status && (
            <Chip
              label={customer.status}
              size="small"
              color={statusColor as "success" | "error" | "warning" | "default"}
              variant={statusColor === "default" ? "outlined" : "filled"}
            />
          )}
        </Stack>
        {usersLabel && (
          <Typography variant="caption" color="text.secondary">
            {usersLabel}
          </Typography>
        )}
      </Box>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(120px, 1fr))",
            lg: "repeat(6, minmax(120px, 1fr))",
          },
          gap: 1.5,
          flex: "2 1 360px",
          minWidth: 0,
        }}
      >
        <MetaItem label={t("profiles.meta.email")} value={customer.email} />
        <MetaItem label={t("profiles.meta.phone")} value={formatCustomerPhone(customer.phone)} />
        <MetaItem label={t("profiles.meta.mobile")} value={formatCustomerPhone(customer.mobilePhone)} />
        <MetaItem label={t("profiles.meta.contact")} value={customer.pointOfContact} />
        <MetaItem label={t("profiles.meta.workHours")} value={workTime} />
        <MetaItem label={t("profiles.meta.timeZone")} value={customer.timeZone} />
        <MetaItem label={t("profiles.meta.locale")} value={customer.locale} />
        <MetaItem label={t("profiles.meta.address")} value={customer.address} />
      </Box>

      {customer.description && (
        <Box sx={{ width: "100%", pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("profiles.meta.description")}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ mt: 0.25 }}>
            {customer.description}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CustomerDetailsReadOnly;
